<script lang="ts">
  import { onMount } from "svelte";
  import { _ as t } from "svelte-i18n";
  import HomeBanner from "../components/HomeBanner.svelte";
  import HomeTabs from "../components/HomeTabs.svelte";
  import IpoActivityPanel from "../components/IpoActivityPanel.svelte";
  import MyLotteryPanel from "../components/MyLotteryPanel.svelte";
  import StickyActionBar from "../components/StickyActionBar.svelte";
  import { storageModel, LangEnums } from "../lib/storage-manager";
  import {
    getActivityDetail,
    getActivityInfo,
    getCurrentUser,
    getCustomerInfo,
    checkRoster,
    type ActivityDetail,
  } from "../lib/restful";
  import {
    isEmbedded as isEmbeddedDevice,
    ChiefBridge,
    shouldOffsetUp,
    shouldUseAppLoading,
    hideAppLoadingDirect,
  } from "../../libs/chief-bridge";
  import { getContainerStyle } from "../../libs/layout";
  import { LOTTERY_CODE_D3 } from "../lib/constants";
  import { generateSharePoster } from "../lib/poster-utils";
  import { bridge } from "../lib/bridge";
  import NavBar from "../../libs/components/NavBar.svelte";
  import lineShareIcon from "../assets/images/line_share.png";

  const isEmbeddedApp = isEmbeddedDevice();
  const needOffsetUp = shouldOffsetUp();
  const useAppLoading = shouldUseAppLoading();

  // 从 URL 获取 lotteryId 参数
  const activityCode =
    new URLSearchParams(window.location.search).get("lotteryid") ||
    LOTTERY_CODE_D3;

  // 从 URL 获取 channel 参数用于埋点
  const fromChannel =
    new URLSearchParams(window.location.search).get("from_channel") || "w";

  // 接口返回的活动数据
  let activityData = $state<ActivityDetail | null>(null);
  let loading = $state(true);
  /** 开户按钮是否置灰：站内根据 userType 判断，站外不置灰（点击跳 App 下载） */
  let openAccountDisabled = $state(isEmbeddedApp);
  /** 入金按钮是否置灰：端内根据白名单判断 */
  let depositDisabled = $state(false);
  /** 交易按钮是否置灰：端内根据白名单判断 */
  let tradeDisabled = $state(false);
  /** 认购按钮是否置灰 */
  let ipoDisabled = $state(false);

  let bannerImage = $derived(activityData?.bannerUrl || "");
  let activityTitle = $derived("");

  let currentTab = $state(1);
  let awardImgUrl = $derived(activityData?.awardImgUrl || "");

  onMount(async () => {
    // App 原生 Loading（App 端有 10 秒超时自动关闭的兜底机制）
    if (useAppLoading) {
      bridge.callNative("startAppLoading");
    }

    if (!activityCode) {
      loading = false;
      hideAppLoadingDirect();
      console.warn("lotteryid is required");
      return;
    }
    try {
      const [activityRes, userRes] = await Promise.all([
        isEmbeddedApp
          ? getActivityDetail(activityCode)
          : getActivityInfo(activityCode),
        isEmbeddedApp ? getCurrentUser() : Promise.resolve(null),
      ]);
      if (activityRes.data.code === 0 && activityRes.data.data) {
        activityData = activityRes.data.data;

        // 预加载图片，等图片加载完再显示页面
        const imageUrls = [
          activityRes.data.data.bannerUrl,
          activityRes.data.data.awardImgUrl,
        ].filter(Boolean) as string[];

        if (imageUrls.length > 0) {
          await Promise.all(
            imageUrls.map(
              (url) =>
                new Promise<void>((resolve) => {
                  const img = new Image();
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                  img.src = url;
                }),
            ),
          );
        }
      }
      // 内嵌时根据 userType 判断开户按钮是否置灰（userType === 0 不置灰）
      if (userRes && userRes.data.code === 0 && userRes.data.data) {
        openAccountDisabled = userRes.data.data.userType !== 0;
      } else if (isEmbeddedApp) {
        // 接口失败或返回异常时降级：不置灰，避免按钮永久不可用
        openAccountDisabled = false;
      }

      // 端内：先查客户类型，非个人用户四个按钮全部置灰
      if (isEmbeddedApp) {
        try {
          const cidRes = await getCustomerInfo();
          const clientType =
            cidRes.data.code === 0 ? cidRes.data.data?.clientType : undefined;
          if (
            clientType !== undefined &&
            clientType !== null &&
            clientType !== 1 &&
            clientType !== 0
          ) {
            // 非个人用户，四个按钮全部置灰，不再走白名单判断
            openAccountDisabled = true;
            depositDisabled = true;
            tradeDisabled = true;
            ipoDisabled = true;
          } else {
            // 个人用户(clientType===1)或clientType===0，走白名单判断入金/交易按钮
            const rosterRes = await checkRoster(
              ["june_lottery_deposit", "june_lottery_trade"],
              1,
            );
            if (rosterRes.data.code === 0 && rosterRes.data.data) {
              for (const item of rosterRes.data.data) {
                // inRoster=true 表示"已在完成名单中"（已完成该任务），按钮置灰不可再点
                if (item.activityCode === "june_lottery_deposit") {
                  depositDisabled = item.inRoster;
                } else if (item.activityCode === "june_lottery_trade") {
                  tradeDisabled = item.inRoster;
                }
              }
            }
          }
        } catch (e) {
          console.error("checkCustomerInfo/checkRoster error:", e);
        }
      }
    } catch (e) {
      console.error("getActivityDetail error:", e);
    } finally {
      loading = false;
      hideAppLoadingDirect();

      // 埋点：page_view 落地页浏览
      bridge.sendDatafinder("page_view", {
        page_name: "抽奖落地页",
        scenes: "抽奖落地页浏览",
        p_id: "lottery_activity",
        m_id: "d3",
        t_id: "w",
        s_id: "w",
        e_id: "w",
        H5_title: activityData?.activityName || "",
        H5_url: window.location.href,
        from_channel: fromChannel,
      });
    }
  });

  function handleClose() {
    bridge.callNative("Route_HomePage");
  }

  function handleGoToRules() {
    const url = new URL(window.location.href);
    url.searchParams.set("rules", "true");
    window.history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  // 分享海报配置
  // 分享海报（根据语言选择背景图）
  import shareBgSc from "../assets/images/share-sc.png";
  import shareBgTc from "../assets/images/share-tc.png";
  import appLogo from "../assets/images/applogo.png";

  function getShareBg(): string {
    const lang = storageModel.language;
    if (lang === LangEnums.zhHK) return shareBgTc;
    return shareBgSc;
  }

  let sharing = $state(false);

  async function handleShare() {
    if (sharing) return;
    sharing = true;
    try {
      const base64 = await generateSharePoster({
        backgroundSrc: getShareBg(),
        width: 1095,
        height: 2295,
        qrcode: { x: 813, y: 1869, size: 190 },
        qrcodeContent: "https://official.chiefgroup.com.hk/chief-download",
        qrcodeLogoSrc: appLogo,
      });
      if (base64) {
        if (isEmbeddedApp) {
          bridge.callNative("share", { type: "poster", base64Image: base64 });
        }
      }
    } catch (e) {
      console.error("generateSharePoster error:", e);
    } finally {
      // 延迟重置，防止原生分享面板弹出期间重复触发
      setTimeout(() => {
        sharing = false;
      }, 1500);
    }
  }

  // IPO 页面 URL（根据环境区分）
  const ipoBaseUrl =
    __MODE__ === "prod"
      ? "https://ipo-h5.chiefgroup.com.hk/"
      : "https://ipo-h5.test.stx365.com/";
  const ipoUrl = ipoBaseUrl + "ipo/index/1";

  // 站外跳转 App 逻辑
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isMobile = isAndroid || isIOS;

  /** 根据平台通过 bridge openLink 跳转（linkType: 3，使用原生 scheme） */
  function navigateInApp(iosUrl: string, androidUrl: string) {
    bridge.callNative("openLink", {
      linkType: 3,
      linkUrl: isIOS ? iosUrl : androidUrl,
    });
  }

  /** 去认购：站内跳 IPO 首页，站外跳 App */
  function handleIpo() {
    // 埋点：element_click 去认购
    bridge.sendDatafinder("element_click", {
      page_name: "抽奖落地页",
      scenes: "抽奖落地页点击",
      element_name: "任务点击",
      p_id: "lottery_activity",
      m_id: "d3",
      t_id: "w",
      s_id: "w",
      e_id: "去认购",
      H5_title: activityData?.activityName || "",
      from_channel: fromChannel,
    });
    if (!isEmbeddedApp) {
      handleGoToApp();
      return;
    }
    navigateInApp(
      `chiefAppNav://ipo?url=${encodeURIComponent(ipoUrl)}`,
      `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({ showTitle: $t("ipo_title"), webType: "Other", url: ipoUrl })}`,
    );
  }

  /** 去开户：站内通过 bridge openLink 跳转开户页，站外跳 App */
  const openAccountUrl =
    __MODE__ === "prod"
      ? "https://service.chiefgroup.com.hk"
      : "https://chief-fe-webapp.test.stx365.com";

  function handleOpenAccount() {
    // 埋点：element_click 去开户
    bridge.sendDatafinder("element_click", {
      page_name: "抽奖落地页",
      scenes: "抽奖落地页点击",
      element_name: "任务点击",
      p_id: "lottery_activity",
      m_id: "d3",
      t_id: "w",
      s_id: "w",
      e_id: "去开户",
      H5_title: activityData?.activityName || "",
      from_channel: fromChannel,
    });
    if (!isEmbeddedApp) {
      handleGoToApp();
      return;
    }
    const title = $t("task_open_account_btn");
    if (isIOS) {
      bridge.callNative("openLink", {
        linkType: 3,
        linkUrl: `chiefAppNav://web?url=${encodeURIComponent(openAccountUrl)}&title=${encodeURIComponent(title)}&displayNav=0&displayAllWebviewToH5=1`,
      });
    } else {
      bridge.callNative("openLink", {
        linkType: 1,
        linkUrl: openAccountUrl,
      });
    }
  }

  /** 获取更多奖励：跳转邀请好友页 */
  const inviteUrl =
    __MODE__ === "prod"
      ? "https://common-h5.chiefgroup.com.hk/invite-friends-h5/"
      : "https://common-h5.test.stx365.com/invite-friends-h5/";

  function handleMoreRewards() {
    if (!isEmbeddedApp) {
      handleGoToApp();
      return;
    }
    navigateInApp(
      "chiefAppNav://inviteFriends",
      `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({ showTitle: $t("invite_friends_title"), type: "Referral", url: inviteUrl })}`,
    );
  }

  // 入金页 URL（根据环境区分）
  const depositUrl =
    __MODE__ === "prod"
      ? "https://chief-deposit.chiefgroup.com.hk/cash/money/type"
      : "https://chief-deposit.test.stx365.com/cash/money/type";

  /** 去入金：站内跳入金页，站外跳 App */
  function handleDeposit() {
    // 埋点：element_click 去入金
    bridge.sendDatafinder("element_click", {
      page_name: "抽奖落地页",
      scenes: "抽奖落地页点击",
      element_name: "任务点击",
      p_id: "lottery_activity",
      m_id: "d3",
      t_id: "w",
      s_id: "w",
      e_id: "去入金",
      H5_title: activityData?.activityName || "",
      from_channel: fromChannel,
    });
    if (!isEmbeddedApp) {
      handleGoToApp();
      return;
    }
    navigateInApp(
      "chiefAppNav://deposit",
      `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({ showTitle: $t("deposit_title"), webType: "Other", url: depositUrl })}`,
    );
  }

  /** 去交易：站内跳市场页，站外跳 App */
  function handleTrade() {
    // 埋点：element_click 去交易
    bridge.sendDatafinder("element_click", {
      page_name: "抽奖落地页",
      scenes: "抽奖落地页点击",
      element_name: "任务点击",
      p_id: "lottery_activity",
      m_id: "d3",
      t_id: "w",
      s_id: "w",
      e_id: "去交易",
      H5_title: activityData?.activityName || "",
      from_channel: fromChannel,
    });
    if (!isEmbeddedApp) {
      handleGoToApp();
      return;
    }
    navigateInApp(
      "chiefAppNav://market",
      "com.app.ui.nav.MarketScreen$MarketMain@{}",
    );
  }

  const IOS_FALLBACK = "https://official.chiefgroup.com.hk/chief-download";
  const ANDROID_FALLBACK = "https://official.chiefgroup.com.hk/chief-download";
  const DESKTOP_URL = "https://official.chiefgroup.com.hk/chief-download";

  function handleGoToApp() {
    if (!isMobile) {
      window.open(DESKTOP_URL, "_blank");
      return;
    }

    const pageUrl = window.location.href;
    const schemeUrl = isIOS
      ? `chiefAppNav://web?url=${encodeURIComponent(pageUrl)}&displayNav=0`
      : `chief://service.chiefgroup.com.hk/dp?isLogin=true&route=${encodeURIComponent(
          `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({ showTitle: "", webType: "Other", url: pageUrl })}`,
        )}`;
    const fallbackUrl = isIOS ? IOS_FALLBACK : ANDROID_FALLBACK;

    let hasLeft = false;
    const onVisibilityChange = () => {
      if (document.hidden) hasLeft = true;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // 尝试 scheme 唤起 App
    window.location.href = schemeUrl;

    // 超时后如果没唤起，跳下载页（iOS 用 itms-apps:// 直接打开 App Store，不会有错误页）
    setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (!hasLeft) {
        window.location.href = fallbackUrl;
      }
    }, 1500);
  }
</script>

<svelte:head>
  <title>{activityTitle}</title>
</svelte:head>

<NavBar
  title={activityTitle}
  onClose={handleClose}
  disableOffsetUp={true}
  bgOpacity={0}
  lightBgColor="#000000"
  lightTextColor="#ffffff"
  darkBgColor="#000000"
  darkTextColor="#ffffff"
>
  {#snippet right()}
    <button
      class="flex items-center justify-center w-[32px] h-[32px]"
      onclick={handleShare}
      disabled={sharing}
    >
      <img src={lineShareIcon} alt="share" class="w-[20px] h-[20px]" />
    </button>
  {/snippet}
  <div
    class="app-root relative box-border mx-auto"
    style={needOffsetUp ? "margin-top: -44px;" : ""}
  >
    {#if loading && !useAppLoading}
      <div class="flex items-center justify-center min-h-[80vh]">
        <div class="loading-spinner"></div>
      </div>
    {:else if !loading}
      <HomeBanner
        bannerSrc={bannerImage}
        {activityData}
        onRulesClick={handleGoToRules}
      />

      <div
        class="relative !px-[16px] py-3 rounded-t-[24px] bg-white pt-[14px] shadow-[0_-12px_30px_rgba(19,34,94,0.08)]"
        style="margin-top: -6.5%;"
      >
        <HomeTabs
          {currentTab}
          firstLabel={activityData?.activityPromotionShowName || ""}
          secondLabel={activityData?.myLotteryShowName || ""}
          onChange={(tab) => {
            if (!isEmbeddedApp && tab === 2) {
              handleGoToApp();
              return;
            }
            currentTab = tab;
          }}
        />

        <div class:hidden={currentTab !== 1}>
          <IpoActivityPanel
            imageSrc={awardImgUrl}
            {openAccountDisabled}
            {depositDisabled}
            {tradeDisabled}
            {ipoDisabled}
            onOpenAccount={handleOpenAccount}
            onDeposit={handleDeposit}
            onTrade={handleTrade}
            onIpo={handleIpo}
            onMoreRewards={handleMoreRewards}
          />
        </div>
        <div class:hidden={currentTab !== 2}>
          <MyLotteryPanel
            {activityData}
            {openAccountDisabled}
            {depositDisabled}
            {tradeDisabled}
            {ipoDisabled}
            onOpenAccount={handleOpenAccount}
            onDeposit={handleDeposit}
            onTrade={handleTrade}
            onIpo={handleIpo}
            onMoreRewards={handleMoreRewards}
          />
        </div>

        <p
          class="text-[12px] font-normal leading-[18px] text-text-h2 px-[4px]"
          style={isMobile ? "margin-bottom: 30px;" : ""}
        >
          {$t("compliance_disclaimer")}
        </p>

        {#if !isEmbeddedApp}
          <div class="pb-[90px]"></div>
        {:else}
          <div class="pb-[40px]"></div>
        {/if}
      </div>

      {#if !isEmbeddedApp}
        <div
          class="fixed bottom-0 left-1/2 z-40 w-full -translate-x-1/2 px-[16px] pb-[calc(env(safe-area-inset-bottom)+16px)] pt-[12px] bg-white"
          style={getContainerStyle()}
        >
          <button
            class="w-full py-[14px] rounded-[34px] bg-[linear-gradient(135deg,#2046E6_0%,#3F73FF_100%)] text-white text-[16px] font-semibold"
            type="button"
            onclick={handleGoToApp}
          >
            {$t("cta_go_app")}
          </button>
        </div>
      {/if}
    {/if}
  </div>
</NavBar>

<style>
  .app-root {
    /* 宽度由 getContainerStyle() 动态控制 */
  }
</style>
